import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

export default function AdminModal({ open, onClose }) {
  const [pending, setPending] = useState([]);
  const [activity, setActivity] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, a, m] = await Promise.all([
        api.get("/admin/pending"),
        api.get("/admin/activity"),
        api.get("/admin/users"),
      ]);
      setPending(p.data.pending);
      setActivity(a.data.activity);
      setMembers(m.data.users);
    } catch (e) {
      toast.error("Could not load admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    load();
    window.__lenis?.stop();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [open, load, onClose]);

  const act = async (userId, action) => {
    try {
      await api.post(`/admin/users/${userId}/${action}`);
      setPending((p) => p.filter((u) => u.id !== userId));
      toast.success(action === "approve" ? "Member approved" : "Sign-up rejected");
    } catch (e) {
      toast.error("Action failed");
    }
  };

  const removeUser = async (userId) => {
    try {
      await api.delete(`/admin/users/${userId}`);
      setMembers((ms) => ms.filter((u) => u.id !== userId));
      toast.success("Member deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="admin-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          className="fixed inset-0 z-[80] grid place-items-center bg-black/70 p-5 backdrop-blur-sm"
        >
          <motion.div
            key="admin-card"
            data-testid="admin-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="w-full max-w-md border border-[#27272A] bg-[#0D0D0D]"
          >
            <div className="flex items-center justify-between border-b border-[#27272A] px-6 py-4">
              <span className="flex items-center gap-2 font-jb text-[10px] tracking-[0.35em] text-zinc-500">
                <ShieldCheck className="h-3.5 w-3.5 text-[#FF3B30]" />
                ADMIN — PENDING SIGN-UPS
              </span>
              <button
                data-testid="admin-close-btn"
                onClick={onClose}
                aria-label="Close admin panel"
                className="grid h-8 w-8 place-items-center border border-[#27272A] text-zinc-400 transition-colors duration-300 hover:border-[#FF3B30] hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-[50vh] overflow-y-auto px-6 py-5">
              {loading && pending.length === 0 ? (
                <p className="py-6 text-center font-jb text-[10px] tracking-[0.3em] text-zinc-600">
                  CHECKING…
                </p>
              ) : pending.length === 0 ? (
                <p
                  data-testid="admin-pending-empty"
                  className="py-6 text-center font-jb text-[10px] leading-relaxed tracking-[0.3em] text-zinc-600"
                >
                  NO PENDING SIGN-UPS.
                  <br />
                  THE WATCH DESK IS CLEAR.
                </p>
              ) : (
                <div className="space-y-2">
                  {pending.map((u) => (
                    <div
                      key={u.id}
                      data-testid={`pending-user-${u.id}`}
                      className="flex items-center justify-between gap-3 border border-[#27272A] bg-black/30 px-4 py-3"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-jb text-xs font-bold text-white">
                          {u.name}
                        </p>
                        <p className="truncate font-jb text-[10px] text-zinc-500">
                          {u.email}
                        </p>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <button
                          data-testid={`approve-user-${u.id}`}
                          onClick={() => act(u.id, "approve")}
                          className="flex items-center gap-1.5 border border-[#10B981]/50 bg-[#10B981]/10 px-3 py-1.5 font-jb text-[10px] tracking-[0.15em] text-[#10B981] transition-colors duration-300 hover:bg-[#10B981] hover:text-black"
                        >
                          <Check className="h-3 w-3" />
                          APPROVE
                        </button>
                        <button
                          data-testid={`reject-user-${u.id}`}
                          onClick={() => act(u.id, "reject")}
                          className="flex items-center gap-1.5 border border-[#FF3B30]/50 bg-[#FF3B30]/10 px-3 py-1.5 font-jb text-[10px] tracking-[0.15em] text-[#FF5247] transition-colors duration-300 hover:bg-[#FF3B30] hover:text-black"
                        >
                          <X className="h-3 w-3" />
                          REJECT
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <p className="mt-8 border-t border-[#27272A] pt-5 font-jb text-[10px] tracking-[0.35em] text-[#FF3B30]">
                MEMBERS ({members.length})
              </p>
              <div data-testid="admin-members-list" className="mt-3 space-y-1.5">
                {members.map((m) => (
                  <div
                    key={m.id}
                    data-testid={`member-row-${m.id}`}
                    className="flex items-center justify-between gap-3 border border-[#1C1C1C] bg-black/20 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-jb text-[11px] font-bold text-white">
                        {m.name}
                        {m.role === "admin" && (
                          <span className="text-[#FFC107]"> · ADMIN</span>
                        )}
                        {!m.approved && (
                          <span className="text-[#FF5247]"> · PENDING</span>
                        )}
                      </p>
                      <p className="truncate font-jb text-[10px] text-zinc-500">
                        {m.email}
                      </p>
                    </div>
                    {m.role !== "admin" && (
                      <button
                        data-testid={`delete-user-${m.id}`}
                        onClick={() => removeUser(m.id)}
                        className="shrink-0 border border-[#FF3B30]/50 bg-[#FF3B30]/10 px-3 py-1.5 font-jb text-[10px] tracking-[0.15em] text-[#FF5247] transition-colors duration-300 hover:bg-[#FF3B30] hover:text-black"
                      >
                        DELETE
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <p className="mt-8 border-t border-[#27272A] pt-5 font-jb text-[10px] tracking-[0.35em] text-[#FF3B30]">
                ACTIVITY LOG
              </p>
              <div data-testid="admin-activity-log" className="mt-3 space-y-1.5">
                {activity.length === 0 && (
                  <p className="py-4 text-center font-jb text-[10px] tracking-[0.3em] text-zinc-600">
                    NO ACTIVITY YET.
                  </p>
                )}
                {activity.map((a, i) => (
                  <div
                    key={`${a.at}-${i}`}
                    data-testid={`activity-item-${i}`}
                    className="border border-[#1C1C1C] bg-black/20 px-3 py-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate font-jb text-[10px] font-bold tracking-[0.1em] text-[#FFC107]">
                        {a.actor.toUpperCase()}
                      </span>
                      <span className="shrink-0 font-jb text-[9px] tracking-[0.1em] text-zinc-600">
                        {new Date(a.at).toLocaleString([], {
                          day: "2-digit",
                          month: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <p className="mt-0.5 font-jb text-[10px] tracking-[0.1em]">
                      <span
                        className={
                          a.event === "LOGIN_FAILED" || a.event === "REJECT" || a.event === "LOGIN_BLOCKED"
                            ? "text-[#FF5247]"
                            : a.event === "APPROVE" || a.event === "LOGIN"
                              ? "text-[#10B981]"
                              : "text-zinc-300"
                        }
                      >
                        {a.event}
                      </span>
                      {a.detail && (
                        <span className="text-zinc-500"> — {a.detail}</span>
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-[#27272A] px-6 py-4">
              <button
                data-testid="admin-refresh-btn"
                onClick={load}
                className="w-full border border-zinc-700 py-2.5 font-jb text-[10px] tracking-[0.3em] text-zinc-400 transition-colors duration-300 hover:border-[#FF3B30] hover:text-white"
              >
                REFRESH
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
